import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Anchor,
  MapPin,
  Shield,
  Users,
  Clock,
  Ship,
  Sparkles,
  MessageCircle,
  Package,
} from "lucide-react";
import IndiaBoatsTopBar from "../Components/IndiaBoatsTopBar";
import PremiumNavbar from "../Components/PremiumNavbar";
import Footer from "../Components/Footer";
import FAQSection from "../Components/FAQSection";
import SEO from "../Components/SEO";
import { SEO_DATA } from "../config/seoData";
import { ALIBAUG_ELEPHANTA_FAQS } from "../config/seoSchemas";

const WHATSAPP_URL = "https://wa.me/918591993301";

const trustItems = [
  "Private Boat Transfers",
  "Mumbai to Mandwa",
  "Elephanta Trips",
  "Multiple Boat Sizes",
  "Guest Safety First",
];

const mandwaPerfectFor = [
  "Day trips to Alibaug",
  "Lunch outings",
  "Resort visits",
  "Family escapes",
  "Couples and private groups",
  "Business meetings",
  "Weekend getaways",
];

const elephantaIdealFor = [
  "Families",
  "Tourists visiting Mumbai",
  "Couples",
  "Small private groups",
  "International visitors",
  "Private sightseeing experiences",
];

const boatCapacities = [
  "5 Seater Speed Boat",
  "6 Seater Speed Boat",
  "8 Seater Speed Boat",
  "10 Seater Speed Boat",
  "12 Seater Speed Boat",
  "15 Seater Speed Boat",
  "18 Seater Speed Boat",
  "20 Seater Speed Boat",
];

const curatedPackages = [
  {
    title: "Mumbai to Mandwa Curated Day Package",
    description:
      "Private speed boat transfers, local vehicle movement and time to explore Mandwa and Alibaug.",
    image: "/assets/img/alibaug_package.png",
  },
  {
    title: "Mumbai to Elephanta Curated Island Package",
    description:
      "Private speed boat, required permissions, local guide and sightseeing time on Elephanta Island.",
    image: "/assets/img/elephanta_package.png",
  },
  {
    title: "Mumbai to Mandwa with Sunset Yacht Return",
    description:
      "Travel to Mandwa by speed boat, enjoy your day ashore and return to Mumbai aboard a sail yacht in the evening.",
    image: "/assets/img/speed_boat_img1.png",
  },
  {
    title: "Mumbai to Mandwa Overnight Yacht Experience",
    description:
      "Sail from Mumbai to Mandwa, stay overnight aboard the yacht and return to Mumbai the following morning.",
    image: "/assets/img/speed_boat_img2.png",
  },
];

const whyTravel = [
  {
    title: "Private Experience",
    description:
      "Travel with your own group instead of sharing the journey with a large crowd.",
    icon: Users,
  },
  {
    title: "Quicker Sea Route",
    description:
      "Reach Mandwa and other coastal destinations without spending hours travelling through road traffic.",
    icon: Clock,
  },
  {
    title: "Multiple Boat Options",
    description:
      "Choose a boat based on your guest count and travel requirement.",
    icon: Ship,
  },
  {
    title: "Experienced Crew",
    description:
      "Our marine team focuses on smooth operations, guest assistance and safe boating practices.",
    icon: Shield,
  },
  {
    title: "Memorable Journey",
    description:
      "Mumbai Harbour, the coastline and the open sea turn the transfer itself into part of your experience.",
    icon: Sparkles,
  },
];

const AlibaugElephantaBoatTours = () => {
  return (
    <div className="ibaet-page">
      <SEO {...SEO_DATA.alibaugElephantaBoatTours} />
      <IndiaBoatsTopBar />
      <PremiumNavbar />

      <section className="ibaet-hero">
        <div
          className="ibaet-hero-bg"
          style={{ backgroundImage: "url(/assets/img/alibaug-elephanta.jpeg)" }}
        />
        <div className="ibaet-hero-overlay" />
        <div className="ibaet-hero-inner">
          <span className="ibaet-eyebrow">Island Escapes from Mumbai</span>
          <h1 className="ibaet-hero-title">
            Alibaug &amp; Elephanta Boat Tours from Mumbai
          </h1>
          <p className="ibaet-hero-subtitle">
            Escape the city with quick island getaways and scenic boat
            experiences from Mumbai to Alibaug, Mandwa and Elephanta.
          </p>
          <p className="ibaet-hero-highlight">
            Skip the traffic. Explore Mumbai&apos;s best island escapes in style.
          </p>
          <div className="ibaet-hero-actions">
            <Link to="/SpeedBoat" className="ibaet-btn ibaet-btn-primary">
              Book a Speed Boat
              <ArrowRight size={18} />
            </Link>
            <Link to="/packages" className="ibaet-btn ibaet-btn-secondary">
              Explore Curated Packages
            </Link>
          </div>
          <div className="ibaet-trust-strip">
            {trustItems.map((item) => (
              <span key={item} className="ibaet-trust-item">{item}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="ibaet-destinations">
        <div className="ibaet-container">
          <div className="ibaet-dest-grid">
            <article className="ibaet-dest-card">
              <img
                src="/assets/img/alibaug_package.png"
                alt="Private speed boat from Mumbai to Mandwa for Alibaug"
                className="ibaet-dest-image"
              />
              <div className="ibaet-dest-content">
                <h2>Mandwa &amp; Alibaug</h2>
                <p>
                  Private speed boat transfers from Mumbai to Mandwa for a faster
                  coastal route to Alibaug and nearby getaways.
                </p>
                <Link to="/SpeedBoat" className="ibaet-link-btn">
                  View Mumbai to Mandwa Speed Boats
                  <ArrowRight size={16} />
                </Link>
              </div>
            </article>

            <article className="ibaet-dest-card">
              <img
                src="/assets/img/elephanta_package.png"
                alt="Private boat trip from Mumbai to Elephanta Island"
                className="ibaet-dest-image"
              />
              <div className="ibaet-dest-content">
                <h2>Elephanta Island</h2>
                <p>
                  A private harbour crossing to Elephanta for scenic island
                  sightseeing and curated day experiences.
                </p>
                <Link to="/SpeedBoat" className="ibaet-link-btn">
                  Explore Elephanta Boat Options
                  <ArrowRight size={16} />
                </Link>
              </div>
            </article>
          </div>

          <div className="ibaet-speed-cta-wrap">
            <Link to="/SpeedBoat" className="ibaet-btn ibaet-btn-primary ibaet-btn-large">
              Choose Your Speed Boat
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="ibaet-intro">
        <div className="ibaet-container ibaet-intro-grid">
          <div>
            <span className="ibaet-eyebrow ibaet-eyebrow-dark">Leave the City Behind</span>
            <h2 className="ibaet-section-title">
              One of the best ways to experience Mumbai is from the water.
            </h2>
          </div>
          <div className="ibaet-intro-copy">
            <p>
              With Indiaboats, you can travel by private speed boat from Mumbai to
              Mandwa for Alibaug or head across Mumbai Harbour to Elephanta Island
              for a scenic day trip.
            </p>
            <p>
              Whether you are planning a relaxed coastal escape, family outing,
              private group trip, sightseeing experience or simply want to avoid
              long road journeys, our boat experiences make the journey part of
              the adventure.
            </p>
          </div>
        </div>
      </section>

      <section className="ibaet-split">
        <div className="ibaet-container ibaet-split-layout">
          <div className="ibaet-split-media">
            <img
              src="/assets/img/alibaug_package.png"
              alt="Mandwa and Alibaug day trip by private speed boat from Mumbai"
            />
          </div>
          <div className="ibaet-split-content">
            <span className="ibaet-eyebrow ibaet-eyebrow-dark">Mumbai to Mandwa</span>
            <h2 className="ibaet-section-title">
              Mumbai to Mandwa &amp; Alibaug by Speed Boat
            </h2>
            <p>
              Travel from Mumbai to Mandwa by private speed boat and enjoy a
              faster, more scenic way to reach the Alibaug side of the coast.
            </p>
            <h3 className="ibaet-list-title">Perfect For</h3>
            <ul className="ibaet-check-list">
              {mandwaPerfectFor.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="ibaet-note">
              Once you reach Mandwa, you can continue onwards to Alibaug or nearby
              destinations by road.
            </p>
            <Link to="/SpeedBoat" className="ibaet-link-btn">
              View Mumbai to Mandwa Speed Boats
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="ibaet-split ibaet-split-reverse">
        <div className="ibaet-container ibaet-split-layout">
          <div className="ibaet-split-media">
            <img
              src="/assets/img/elephanta_package.png"
              alt="Private boat trip from Mumbai to Elephanta Island"
            />
          </div>
          <div className="ibaet-split-content">
            <span className="ibaet-eyebrow ibaet-eyebrow-dark">Elephanta Island</span>
            <h2 className="ibaet-section-title">
              Mumbai to Elephanta Island by Private Boat
            </h2>
            <p>
              Discover one of Mumbai&apos;s most iconic island destinations with a
              private boat journey across the harbour to Elephanta.
            </p>
            <p>
              The sea route gives you a unique view of Mumbai&apos;s waterfront before
              arriving at Elephanta Island, where guests can spend time exploring
              the island and its historic surroundings.
            </p>
            <h3 className="ibaet-list-title">Ideal For</h3>
            <ul className="ibaet-check-list">
              {elephantaIdealFor.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="ibaet-note">
              For guests who prefer everything arranged in one experience,
              Indiaboats also offers curated Elephanta packages with local
              sightseeing support.
            </p>
            <Link to="/SpeedBoat" className="ibaet-link-btn">
              Explore Elephanta Boat Options
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="ibaet-boats">
        <div className="ibaet-container">
          <div className="ibaet-section-head">
            <span className="ibaet-eyebrow ibaet-eyebrow-dark">Choose Your Boat</span>
            <h2 className="ibaet-section-title">
              Multiple speed boat options for your group size
            </h2>
            <p className="ibaet-section-text">
              Indiaboats offers multiple speed boat options depending on your
              group size. For larger groups, ferry and larger-capacity boat
              options may also be available.
            </p>
          </div>
          <div className="ibaet-capacity-grid">
            {boatCapacities.map((capacity) => (
              <div key={capacity} className="ibaet-capacity-card">
                <Anchor size={18} />
                <span>{capacity}</span>
              </div>
            ))}
          </div>
          <div className="ibaet-center-cta">
            <Link to="/SpeedBoat" className="ibaet-btn ibaet-btn-primary">
              View All Speed Boats
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="ibaet-packages">
        <div className="ibaet-container">
          <div className="ibaet-section-head ibaet-section-head-light">
            <span className="ibaet-eyebrow ibaet-eyebrow-light">Premium Upgrade</span>
            <h2 className="ibaet-section-title ibaet-section-title-light">
              Make It More Than Just a Transfer
            </h2>
            <p className="ibaet-section-text ibaet-section-text-light">
              For guests who want a complete day experience, Indiaboats offers
              curated packages that combine private boat travel with destination
              experiences.
            </p>
          </div>
          <div className="ibaet-package-grid">
            {curatedPackages.map((pkg) => (
              <article key={pkg.title} className="ibaet-package-card">
                <img src={pkg.image} alt={pkg.title} className="ibaet-package-image" />
                <div className="ibaet-package-body">
                  <h3>{pkg.title}</h3>
                  <p>{pkg.description}</p>
                  <Link to="/packages" className="ibaet-package-btn">
                    Explore Package
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="ibaet-center-cta">
            <Link to="/packages" className="ibaet-btn ibaet-btn-light">
              Explore Curated Packages
              <Package size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="ibaet-why">
        <div className="ibaet-container">
          <div className="ibaet-section-head">
            <span className="ibaet-eyebrow ibaet-eyebrow-dark">Why Indiaboats</span>
            <h2 className="ibaet-section-title">Why Travel with Indiaboats</h2>
            <p className="ibaet-section-text">
              A private boat journey offers something that ordinary road travel
              cannot.
            </p>
          </div>
          <div className="ibaet-why-grid">
            {whyTravel.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="ibaet-why-card">
                  <div className="ibaet-why-icon">
                    <Icon size={22} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ibaet-escape">
        <div className="ibaet-container ibaet-escape-inner">
          <div>
            <span className="ibaet-eyebrow ibaet-eyebrow-dark">Quick Escape</span>
            <h2 className="ibaet-section-title">
              Perfect for a Quick Escape from Mumbai
            </h2>
          </div>
          <div className="ibaet-escape-copy">
            <p>
              A private boat trip is ideal when you want to turn an ordinary day
              into something different.
            </p>
            <p>
              Spend the day exploring Alibaug. Visit Elephanta with family. Plan
              lunch across the harbour. Take friends for a coastal escape. Or
              simply enjoy the experience of seeing Mumbai from the sea.
            </p>
            <p className="ibaet-escape-lead">
              Sometimes the best part of leaving Mumbai is how you get there.
            </p>
          </div>
        </div>
      </section>

      <section className="ibaet-info">
        <div className="ibaet-container">
          <div className="ibaet-info-card">
            <div className="ibaet-info-icon">
              <MapPin size={22} />
            </div>
            <div>
              <h2 className="ibaet-section-title">Important Travel Information</h2>
              <ul className="ibaet-info-list">
                <li>
                  Boat operations depend on weather, tide, sea conditions, local
                  permissions and safety clearance.
                </li>
                <li>
                  Departure timing and travel duration may vary according to marine
                  and operational conditions.
                </li>
                <li>
                  Guests should follow all safety instructions provided by the
                  captain and crew.
                </li>
                <li>
                  For the smoothest experience, arrive at the reporting point at
                  the advised time before departure.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        title="Frequently Asked Questions"
        faqData={ALIBAUG_ELEPHANTA_FAQS}
      />

      <section className="ibaet-final-cta">
        <div className="ibaet-container">
          <div className="ibaet-final-card">
            <span className="ibaet-final-badge">Escape Mumbai. Start with the Sea.</span>
            <h2>Make the journey as memorable as the destination</h2>
            <p>
              Whether you are heading to Mandwa, Alibaug or Elephanta, choose your
              boat, gather your group and let Indiaboats take you across Mumbai
              Harbour.
            </p>
            <div className="ibaet-final-actions">
              <Link to="/SpeedBoat" className="ibaet-btn ibaet-btn-light">
                Book a Speed Boat
                <ArrowRight size={18} />
              </Link>
              <Link to="/packages" className="ibaet-btn ibaet-btn-ghost">
                Explore Curated Packages
              </Link>
              <a
                href={WHATSAPP_URL}
                className="ibaet-btn ibaet-btn-ghost"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle size={18} />
                Enquire on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlibaugElephantaBoatTours;
